/* @flow */
/* eslint sort-keys: "error" */

import gql from 'graphql-tag';
import {srsParams} from '../utils/searchParamsUtils';
import {fragments as hitCollectionFragments} from '../components/HitCollection/HitCollection';
import {fragments as mapFragments} from '../components/Map/Map';
import {fragments as pagerFragments} from '../components/Pager/Pager';
import {fragments as destinationMessage} from '../components/SearchBannerContainer/destinationMessage';
import {fragments as filterCountsFragments} from '../../universal/queries/filteredCountQuery';
import {fragments as expandedGroupsFragments} from '../components/ExpandedGroups/ExpandedGroups';
import {fragments as htmlTitleFragments} from '../components/HtmlTitle/HtmlTitle';
import {fragments as analyticsDataLayerFragments} from '../components/AnalyticsDataLayer/AnalyticsDataLayer';
import {fragments as seoLinkpileFragments} from '../components/SeoLinkpile/SeoLinkpile';
import {fragments as searchTermCarouselFragments} from '../components/SearchTermCarousel/SearchTermCarousel';

import type {
    SearchParamsType,
    SearchRequestType
} from '../reducers/searchParams';
import type {FilterGroupType} from '../components/MoreFilters/MoreFilters';
import type {ListingShape} from '../components/Hit/Hit';
import type {MapViewport} from '../components/Map/Map';
import type {Shape as ExpandedGroupsShape} from '../components/ExpandedGroups/ExpandedGroups';
import type DestinationMessage from '@homeaway/graphql-traveler-urgency-messaging';
import type {AlternativeLink} from '../components/HtmlTitle/HtmlTitle';
import type {Props as SeoLinkpile} from '../components/SeoLinkpile/SeoLinkpile';
import type {PinnedListing} from '../components/HitCollection/HitCollection';
import type DiscoveryFeeds from '@homeaway/graphql-traveler-discovery';

export type SearchReview = {
    detailPageUrl: string,
    reviewHead: string,
    reviewText: string,
    reviewCreated: string,
    reviewArrival: string,
    reviewRating: number,
    hitThumbnailUrl: string
};

export type Coordinates = {
    latitude: number,
    longitude: number
};

// typed based on shape of `querySelectionSet` below
export type Geography = {
    name?: string,
    description?: string,
    location?: Coordinates,
    isGeocoded?: boolean,
    shouldShowMapCentralPin: boolean,
    lbsId?: string,
    primaryGeoType?: string,
    breadcrumbs: Geography[],
    countryCode?: string
};

type Internal = {
    searchServiceUrl: string
};

export type BreadcrumbShape = {
    url: string,
    name: string
};

export type DestinationBreadcrumbShape = {
    name: string,
    url: string
};

export type DestinationShape = {
    breadcrumbs?: BreadcrumbShape[],
    links?: BreadcrumbShape[]
};

export type CoreFilters = {
    adults?: number,
    children?: number,
    pets?: number,
    minBedrooms?: number,
    maxBedrooms?: number,
    minSleeps?: number,
    maxSleeps?: number,
    minPrice?: number,
    maxPrice?: number,
    minBathrooms?: number,
    maxBathrooms?: number
};

export type SearchDateRange = {
    arrivalDate: string,
    departureDate: string
};

export type SearchSort =
    | 'DEFAULT_SORT'
    | 'RATING_DESC'
    | 'BEDROOMS_DESC'
    | 'BEDROOMS_ASC'
    | 'PRICE_DESC'
    | 'PRICE_ASC'
    | 'NUM_REVIEWS_DESC';

// typed based on shape of `querySelectionSet` below
export type TypeaheadSuggestion = {
    uuid: string,
    term: string,
    name: string
};

export type ParsedSearchParams = {
    q?: string,
    coreFilters?: CoreFilters,
    dates?: SearchDateRange,
    sort: SearchSort
};

/**
 * This type represents the result of the main search query. If you update the
 * query below, you must keep this type in sync to surface potential errors
 * throughout the rest of the codebase that unit tests may not catch.
 */
export type SearchQueryResults = {
    alternativeLinks?: AlternativeLink[],
    canonicalLink?: string,
    destination?: DestinationShape,
    destinationBreadcrumbs?: DestinationBreadcrumbShape[],
    destinationMessage?: DestinationMessage,
    discoveryXploreFeeds?: DiscoveryFeeds,
    expandedGroups?: ExpandedGroupsShape,
    filterGroups?: FilterGroupType[],
    geography?: ?Geography,
    headlineTitle?: string,
    internal?: Internal,
    listings?: ListingShape[],
    liveRegionRedirectUrl?: string,
    mapViewport: MapViewport,
    metaDescription?: string,
    metaKeywords?: string,
    page?: number,
    pageCount?: number,
    pageSize?: number,
    pageTitle?: string,
    parsedParams?: ParsedSearchParams,
    pidRedirectUrl?: string,
    pinnedListing?: PinnedListing,
    resultCount?: number,
    searchReviews?: SearchReview[],
    searchReviewsHeadline?: string,
    seoLinkpile?: SeoLinkpile,
    typeaheadSuggestion?: ?TypeaheadSuggestion
};

export type SearchQueryResponseData = {
    results: ?SearchQueryResults,
    requestmarker: string,
    error?: Error,
    loading: boolean,
    variables: {
        params?: SearchParamsType, // SearchQuery params
        request?: SearchRequestType, // SearchRequestQuery params
        url?: string
    }
};

export const searchQueryVariables = (params: SearchParamsType, url: string) => {
    let include = ['percentBooked'];
    return {
        params: {
            ...srsParams(params),
            include
        },
        url
    };
};

export const querySelectionSet = `
    id
    typeaheadSuggestion {
        uuid
        term
        name
    }
    geography {
        lbsId
        location {
            latitude
            longitude
        }
        isGeocoded
        shouldShowMapCentralPin
    }
`;

export const FORCE_GRAPHQL_ERROR_HEADER = 'ha-serp-force-graphql-err';
export const SEARCH_QUERY_NAME = 'SearchQuery';

/**
 * IMPORTANT: If you make changes to the shape of this query, please update the
 * SearchQueryResults type above for consistency. We rely on the Flow types
 * being accurate to catch bugs and breaking changes.
 */
const searchQuery = gql`
    query ${SEARCH_QUERY_NAME}($params: ResultsParams, $url: String) {
        results(params: $params) {
            destinationBreadcrumbs {
                name
                url
            }
            headlineTitle
            internal {
                searchServiceUrl
            }
            liveRegionRedirectUrl
            pidRedirectUrl
            searchReviewsHeadline
            searchReviews {
                detailPageUrl
                reviewHead
                reviewText
                reviewCreated
                reviewArrival
                reviewRating
                hitThumbnailUrl
            }
            ${querySelectionSet}
            ...ExpandedGroupsResults
            ...PagerResults
            ...DestinationMessageResults
            ...FilterCountsResults
            ...HitCollectionResults
            ...HtmlTitleResults
            ...ADLResults
            ...MapResults
            ...SeoLinkpileResults
            ...SearchTermCarouselResults
        }
        ...RequestMarkerFragment
    }
    ${hitCollectionFragments.results}
    ${expandedGroupsFragments.results}
    ${filterCountsFragments.results}
    ${mapFragments.results}
    ${pagerFragments.results}
    ${htmlTitleFragments.results}
    ${destinationMessage.results}
    ${seoLinkpileFragments.results}
    ${analyticsDataLayerFragments.results}
    ${analyticsDataLayerFragments.query}
    ${searchTermCarouselFragments.results}
`;
// psst! did you update this query? see the note above.

export default searchQuery;
